/**
 * LA PAGINATION D'UNE LISTE DE LA CONSOLE — demande du fondateur, 16 oct. 2026
 * (AB#880) : « la liste des chapitres fait une longue liste glissante ; la
 * mettre sur plusieurs pages, avec des boutons entre les pages ».
 *
 * Côté CLIENT : `GET /v1/tuteur/manuel/chapitres` rend tout le manuel en une
 * réponse (moins d'une centaine de lignes), et les filtres de « Contenus »
 * s'appliquent déjà sur cette liste entière. On pagine le RÉSULTAT FILTRÉ.
 *
 * La page vit dans l'adresse (`?page=3`) : un retour arrière depuis un
 * chapitre ramène à la page qu'on quittait. La page 1 n'écrit rien — l'adresse
 * d'une liste non feuilletée reste celle d'avant.
 *
 * Tout ce qui se calcule est ici, pur ; le composant est `components/Pagination.tsx`.
 */

/** Une vingtaine de lignes : un écran sans glisser loin, et peu de pages. */
export const TAILLE_PAGE = 20;

/** Le nom du paramètre d'adresse. */
export const PARAM_PAGE = 'page';

export interface PageDeListe<T> {
  /** Les éléments de la page affichée. */
  readonly elements: readonly T[];
  /** La page affichée, de 1 à `nbPages` — ramenée dans les bornes. */
  readonly page: number;
  /** Au moins 1, même pour une liste vide. */
  readonly nbPages: number;
  /** Rang (de 1) du premier élément affiché ; 0 si la liste est vide. */
  readonly premier: number;
  /** Rang (de 1) du dernier élément affiché ; 0 si la liste est vide. */
  readonly dernier: number;
  readonly total: number;
}

/**
 * Découpe une liste. Une page demandée hors des bornes (adresse ancienne, filtre
 * qui a réduit la liste) est RAMENÉE à la plus proche : jamais une page vide
 * quand il y a des lignes.
 */
export function paginer<T>(
  liste: readonly T[],
  pageDemandee: number,
  taille: number = TAILLE_PAGE,
): PageDeListe<T> {
  const total = liste.length;
  const nbPages = Math.max(1, Math.ceil(total / taille));
  const entiere = Number.isFinite(pageDemandee) ? Math.trunc(pageDemandee) : 1;
  const page = Math.min(nbPages, Math.max(1, entiere));
  const debut = (page - 1) * taille;
  const elements = liste.slice(debut, debut + taille);
  return {
    elements,
    page,
    nbPages,
    premier: total === 0 ? 0 : debut + 1,
    dernier: debut + elements.length,
    total,
  };
}

/** Lit la page de l'adresse ; absente ou illisible, c'est la page 1. */
export function lirePage(p: URLSearchParams, param: string = PARAM_PAGE): number {
  const n = Number.parseInt(p.get(param) ?? '', 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * Les paramètres d'adresse pour aller à `page` — les autres (filtres) sont gardés.
 * `param` : une page qui porte PLUSIEURS listes paginées (la fiche d'un
 * chapitre) donne à chacune son paramètre (`p-erreurs`), pour qu'aucune ne
 * feuillette l'autre (AB#888).
 */
export function adresseDePage(
  p: URLSearchParams,
  page: number,
  param: string = PARAM_PAGE,
): URLSearchParams {
  const suite = new URLSearchParams(p);
  if (page <= 1) suite.delete(param);
  else suite.set(param, String(page));
  return suite;
}

/**
 * Les numéros de page à montrer entre « Précédent » et « Suivant » (AB#888 :
 * « pas de navigation entre pages avec numéro »). Jusqu'à 7 pages, toutes ;
 * au-delà, la première, la dernière, la courante et ses voisines, et `null`
 * pour un saut (« … »). Jamais deux sauts côte à côte, jamais un saut d'une
 * seule page (on montre alors le numéro).
 */
export function numerosDePages(page: number, nbPages: number): (number | null)[] {
  if (nbPages <= 7) return Array.from({ length: nbPages }, (_, i) => i + 1);
  const gardes = new Set([1, nbPages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((n) => gardes.add(n));
  if (page >= nbPages - 2) [nbPages - 3, nbPages - 2, nbPages - 1].forEach((n) => gardes.add(n));
  const tries = [...gardes].filter((n) => n >= 1 && n <= nbPages).sort((a, b) => a - b);
  const sortie: (number | null)[] = [];
  for (const n of tries) {
    const avant = sortie.length === 0 ? null : sortie[sortie.length - 1];
    if (typeof avant === 'number' && n - avant === 2) sortie.push(avant + 1);
    else if (typeof avant === 'number' && n - avant > 2) sortie.push(null);
    sortie.push(n);
  }
  return sortie;
}

/** La page (de 1) où tombe l'élément de rang `index` (de 0) ; 1 pour un rang absent (-1). */
export function pageDeLIndex(index: number, taille: number = TAILLE_PAGE): number {
  return index < 0 ? 1 : Math.floor(index / taille) + 1;
}

/** « 41 à 60 sur 87 chapitres » ; « 1 chapitre » ; « Aucun chapitre ». */
export function direEtendue(
  p: Pick<PageDeListe<unknown>, 'premier' | 'dernier' | 'total'>,
  singulier: string,
  pluriel = `${singulier}s`,
): string {
  if (p.total === 0) return `Aucun ${singulier}`;
  const nom = p.total > 1 ? pluriel : singulier;
  if (p.premier === p.dernier) return `${p.premier} sur ${p.total} ${nom}`;
  return `${p.premier} à ${p.dernier} sur ${p.total} ${nom}`;
}
